import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiLock, FiSave, FiUser, FiX } = FiIcons;

const ADMIN_EMAIL_DOMAIN = 'admin.haciendaelgallo.net';
const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,30}$/;

export default function AdminProfile({ session, profile, onClose, onUpdated }) {
  const [username, setUsername] = useState(profile.username || 'haciendaelgallo');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [status, setStatus] = useState({ error: '', success: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setUsername(profile.username || 'haciendaelgallo');
  }, [profile.username]);

  const saveProfile = async (event) => {
    event.preventDefault();
    const cleanUsername = username.trim().toLowerCase();

    if (!USERNAME_PATTERN.test(cleanUsername)) {
      setStatus({
        error: 'Usa entre 3 y 30 letras, números, puntos o guiones.',
        success: ''
      });
      return;
    }

    if (password && password.length < 6) {
      setStatus({
        error: 'La contraseña debe tener al menos 6 caracteres.',
        success: ''
      });
      return;
    }

    if (password && password !== confirmation) {
      setStatus({
        error: 'Las contraseñas no coinciden.',
        success: ''
      });
      return;
    }

    setSaving(true);
    setStatus({ error: '', success: '' });

    const nextEmail = `${cleanUsername}@${ADMIN_EMAIL_DOMAIN}`;

    // 1. Actualizar contraseña si se proporcionó una nueva
    if (password) {
      const { error: pwdError } = await supabase.auth.updateUser({
        password: password
      });

      if (pwdError) {
        setStatus({
          error: `Error al actualizar la contraseña: ${pwdError.message}`,
          success: ''
        });
        setSaving(false);
        return;
      }
    }

    // 2. Actualizar datos en la tabla de administradores
    const { error: profileError } = await supabase
      .from('chat_admins_1786723472726')
      .update({
        username: cleanUsername,
        email: nextEmail
      })
      .eq('user_id', session.user.id);

    if (profileError) {
      if (profileError.code === '23505') {
        setStatus({
          error: 'Ese nombre de usuario ya está en uso por otro administrador.',
          success: ''
        });
      } else {
        setStatus({
          error: 'No fue posible actualizar el perfil de administrador.',
          success: ''
        });
      }
      setSaving(false);
      return;
    }

    setPassword('');
    setConfirmation('');
    setStatus({
      error: '',
      success: '¡Credenciales actualizadas correctamente!'
    });
    onUpdated({ username: cleanUsername, email: nextEmail });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <section className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#111] p-6 shadow-2xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-white">
              Perfil administrativo
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Cambia tu usuario y contraseña de acceso.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-white/5 hover:text-white"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <form onSubmit={saveProfile} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs text-gray-400">
              Nombre de usuario
            </span>
            <div className="flex items-center rounded-xl border border-white/10 bg-white/5 px-3 focus-within:border-red-600 transition-colors">
              <SafeIcon icon={FiUser} className="text-gray-500" />
              <input
                required
                minLength={3}
                maxLength={30}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                className="w-full bg-transparent px-3 py-3 text-sm text-white outline-none"
              />
            </div>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs text-gray-400">
              Nueva contraseña
            </span>
            <div className="flex items-center rounded-xl border border-white/10 bg-white/5 px-3 focus-within:border-red-600 transition-colors">
              <SafeIcon icon={FiLock} className="text-gray-500" />
              <input
                type="password"
                minLength={6}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Déjala vacía para conservarla"
                className="w-full bg-transparent px-3 py-3 text-sm text-white outline-none placeholder:text-gray-600"
              />
            </div>
          </label>

          {password && (
            <label className="block">
              <span className="mb-1.5 block text-xs text-gray-400">
                Confirmar nueva contraseña
              </span>
              <div className="flex items-center rounded-xl border border-white/10 bg-white/5 px-3 focus-within:border-red-600 transition-colors">
                <SafeIcon icon={FiLock} className="text-gray-500" />
                <input
                  type="password"
                  minLength={6}
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  placeholder="Repite la nueva contraseña"
                  className="w-full bg-transparent px-3 py-3 text-sm text-white outline-none placeholder:text-gray-600"
                />
              </div>
            </label>
          )}

          {status.error && (
            <p className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-xs text-red-400">
              {status.error}
            </p>
          )}

          {status.success && (
            <p className="rounded-lg bg-green-500/10 border border-green-500/20 p-3 text-xs text-green-400">
              {status.success}
            </p>
          )}

          <button
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 py-3 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50 transition-all shadow-lg shadow-red-600/20"
          >
            <SafeIcon icon={FiSave} />
            {saving ? 'Guardando...' : 'Guardar credenciales'}
          </button>
        </form>
      </section>
    </div>
  );
}